/* Free shipping progress bar — cart drawer (Modern Roots custom, update-safe).
 *
 * The bar is rendered entirely by Liquid (snippets/cart-free-shipping-bar.liquid). This
 * element only steps in when the shopper's cart currency differs from the store currency
 * and the merchant chose "convert automatically": it converts the base-currency threshold
 * with the live rate Shopify prints on every page (window.Shopify.currency.rate) and
 * rewrites the message and bar width. Because it is a custom element, connectedCallback
 * runs again every time component-cart-drawer.js re-renders the drawer header.
 */
(() => {
  if (customElements.get('cart-free-shipping')) return;

  class CartFreeShipping extends HTMLElement {
    connectedCallback() {
      if (this.dataset.convert !== 'true') return;
      this.update();

      // On first paint this can run before theme.min.js has defined the theme's money
      // formatter; re-run once the page has fully loaded so the amount is formatted the
      // same way as the rest of the drawer.
      if (!this.themeFormatter() && document.readyState !== 'complete') {
        window.addEventListener('load', () => this.update(), { once: true });
      }
    }

    update() {
      const rate = parseFloat(window.Shopify?.currency?.rate);
      const active = (window.Shopify?.currency?.active || '').toUpperCase();
      const baseCents = parseInt(this.dataset.thresholdCents, 10);
      const totalCents = parseInt(this.dataset.totalCents, 10);

      // Only convert when the page's live currency matches the currency the cart was
      // rendered in; otherwise leave the bar hidden rather than show a wrong number.
      if (!rate || !isFinite(rate) || rate <= 0 || !baseCents || active !== this.dataset.cartCurrency) return;

      const thresholdCents = Math.round(baseCents * rate);
      const remaining = Math.max(thresholdCents - totalCents, 0);
      const percent = Math.min(Math.round((totalCents / thresholdCents) * 100), 100);
      const unlocked = remaining === 0;

      const message = this.querySelector('[data-free-shipping-message]');
      const fill = this.querySelector('[data-free-shipping-fill]');
      const track = this.querySelector('[data-free-shipping-track]');

      if (message) {
        if (unlocked) {
          message.textContent = this.dataset.successText || '';
        } else {
          const amount = `<strong class="cart-free-shipping__amount">${this.formatMoney(remaining)}</strong>`;
          message.innerHTML = this.escape(this.dataset.progressText || '').replace('[amount]', amount);
        }
      }
      if (fill) fill.style.width = `${percent}%`;
      if (track) track.setAttribute('aria-valuenow', String(percent));

      this.classList.toggle('cart-free-shipping--unlocked', unlocked);
      this.classList.remove('cart-free-shipping--pending');
    }

    themeFormatter() {
      const fn = window.wetheme?.formatMoney || window.theme?.formatMoney;
      return typeof fn === 'function' ? fn : null;
    }

    formatMoney(cents) {
      // Prefer the theme's own formatter (window.wetheme.formatMoney in Flow) so the
      // output matches the rest of the drawer, e.g. "$12,120.66" with money_format "${{amount}}".
      const themeFormat = this.themeFormatter();
      if (themeFormat) {
        try {
          const out = themeFormat(cents, this.dataset.moneyFormat);
          if (out) return String(out).replace(/ /g, ' ');
        } catch (e) { /* fall through */ }
      }
      try {
        return new Intl.NumberFormat(window.Shopify?.locale || undefined, {
          style: 'currency',
          currency: this.dataset.cartCurrency,
        }).format(cents / 100).replace(/ /g, ' ');
      } catch (e) {
        return `${(cents / 100).toFixed(2)} ${this.dataset.cartCurrency}`;
      }
    }

    escape(str) {
      return str.replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }
  }

  customElements.define('cart-free-shipping', CartFreeShipping);
})();
